import { AxiosRequest } from "_services/api";
import { HttpError } from "_services/api/HttpError";
import { HttpResponse } from "_services/api/HttpResponse";
import { IData, IHttpError } from "_services/api/interface/HttpResponse";
import { usePaginationSortSearch } from "containers/Table/hooks/usePaginationSortSearch";
import { useCallback, useState } from "react";
import { IListarLoteGerenciarPagamento } from "../interface/IListarLoteGerenciarPagamento";
import { IFiltrosGerenciarPagamento } from "../interface/IFiltrosGerenciarPagamento";
import { IHistoricoLote } from "../interface/IHistoricoLote";
import { Data } from "_utils/Data";
import { toastErro } from "components/Toast";

const getMensagemErro = (error: any, fallback: string): string =>
  error?.response?.data?.message ?? fallback;

export function useReprovadosPagamento() {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const { totalRows, setTotalRows } = usePaginationSortSearch();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<IListarLoteGerenciarPagamento[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [filtro, setFiltro] = useState<IFiltrosGerenciarPagamento>({});
  const [pagamentoUrgenteAtivo, setPagamentoUrgenteAtivo] =
    useState<boolean>(false);
  const [ordenacao, setOrdenacao] = useState<{
    campo: string;
    direcao: string;
  } | null>(null);

  const buildParams = (
    obj: Record<string, any>,
    dadosFiltros: IFiltrosGerenciarPagamento,
    urgente?: boolean,
  ) => {
    if (dadosFiltros.idsLotes?.length) {
      obj.lotes = JSON.stringify(dadosFiltros.idsLotes);
    }
    if (dadosFiltros.idsNfRecibo?.length) {
      obj.nfRecibos = JSON.stringify(dadosFiltros.idsNfRecibo);
    }
    if (dadosFiltros.idsProcessos?.length) {
      obj.processos = JSON.stringify(dadosFiltros.idsProcessos);
    }
    if (dadosFiltros.idEmpresa) {
      obj.idEmpresa = dadosFiltros.idEmpresa;
    }
    if (dadosFiltros.idsBeneficiarios?.length) {
      obj.beneficiarios = JSON.stringify(dadosFiltros.idsBeneficiarios);
    }
    if (dadosFiltros.placa) {
      obj.placa = dadosFiltros.placa;
    }
    if (dadosFiltros.idsCustos?.length) {
      obj.custos = JSON.stringify(dadosFiltros.idsCustos);
    }
    if (
      dadosFiltros.possuiArquivoRecibo !== null &&
      dadosFiltros.possuiArquivoRecibo !== undefined
    ) {
      obj.possuiArquivoRecibo = dadosFiltros.possuiArquivoRecibo;
    }
    if (dadosFiltros.idUsuario) {
      obj.idUsuario = dadosFiltros.idUsuario;
    }
    if (
      dadosFiltros.periodo &&
      dadosFiltros.periodo[0] &&
      dadosFiltros.periodo[1]
    ) {
      obj.dataInicioReprovacao = new Data(dadosFiltros.periodo[0]).getDataEua();
      obj.dataFimReprovacao = new Data(dadosFiltros.periodo[1]).getDataEua();
    }
    if (urgente) {
      obj.pagamentoUrgente = true;
    }
    if (ordenacao?.campo && ordenacao?.direcao) {
      obj.ordenarPor = ordenacao.campo;
      obj.ordem = ordenacao.direcao.toUpperCase();
    }

    return Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");
  };

  const listar = useCallback(
    async (dadosFiltros?: IFiltrosGerenciarPagamento, urgente?: boolean) => {
      const filtroAtual = dadosFiltros ?? filtro;
      if (dadosFiltros !== undefined) {
        setFiltro(dadosFiltros);
      }

      setLoading(true);

      const params = buildParams(
        { pagina: page, limite: limit, statusAprovacao: "REPROVADO" },
        filtroAtual,
        urgente ?? pagamentoUrgenteAtivo,
      );

      try {
        const { status, data } = await msAw.get(`/pagamentos/lote?${params}`);
        setDados(data.itens ?? []);
        setTotalRows(data.meta?.total ?? 0);
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        setDados([]);
        setTotalRows(0);
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, filtro, pagamentoUrgenteAtivo, ordenacao],
  );

  const ordenar = (campo: string, direcao: string) => {
    setOrdenacao(campo ? { campo, direcao } : null);
  };

  const excluirLote = useCallback(
    async (idLote: number, observacao: string) => {
      setLoading(true);
      try {
        const { status, data } = await msAw.delete(
          `/pagamentos/lote/${idLote}`,
          {
            data: { observacao },
          },
        );
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        toastErro(getMensagemErro(error, "Erro ao excluir o lote"));
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const buscarHistorico = async (
    idLote: number,
  ): Promise<IHistoricoLote | null> => {
    setLoading(true);
    try {
      const { data } = await msAw.get(`/pagamentos/lote/${idLote}/historico`);
      return data;
    } catch (error) {
      toastErro(getMensagemErro(error, "Erro ao buscar histórico do lote"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const adicionarObservacao = async (idLote: number, observacao: string) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.post(
        `/pagamentos/lote/${idLote}/observacao`,
        {
          observacao,
        },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao salvar observação"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const baixarReciboNf = async (idLote: number, numeroNfRecibo: string) => {
    setLoading(true);
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/imagem/recibo`,
        {
          responseType: "blob",
        },
      );
      const contentType = headers["content-type"] || "application/octet-stream";
      const ext = contentType.split("/").pop()?.split(";")[0] ?? "bin";
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `recibo_nf_${numeroNfRecibo}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastErro(getMensagemErro(error, "Não foi possível baixar o arquivo"));
    } finally {
      setLoading(false);
    }
  };

  const togglePagamentoUrgente = (urgente: boolean) => {
    setPagamentoUrgenteAtivo(urgente);
    listar(filtro, urgente);
  };

  return {
    dados,
    totalRows,
    limit,
    page,
    loading,
    filtro,
    setLimit,
    setPage,
    listar,
    excluirLote,
    buscarHistorico,
    adicionarObservacao,
    baixarReciboNf,
    pagamentoUrgenteAtivo,
    togglePagamentoUrgente,
    ordenacao,
    ordenar,
  };
}
